import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import {
  ParkingCardMode,
  ParkingCardModeDto,
  ParkingCardType,
  ParkingCardTypeDto,
  ParkingGate,
  ParkingGateDto,
  ParkingGateType,
  ParkingGateTypeDto,
  ParkingPaymentLog,
  ParkingPaymentLogDto,
  ParkingVehicleType,
  ParkingVehicleTypeDto,
  ParkingArea,
  ParkingAreaDto,
  ParkingProviderOption,
  ParkingGateGroup,
  ParkingGateGroupDto,
  ParkingGateOption,
  ParkingRateDto,
  ParkingRateMaster,
  TaiwanDiscountOption,
  TaiwanCarCategoryOption,
  ParkingMainRateDto,
  ParkingMainRateMaster,
  ParkingSubRateDto,
  ParkingSubRateMaster,
  ParkingSubRateDetail,
  ParkingSubRateDetailDto,
  ParkingPenaltyRateDto,
  ParkingPenaltyRateMaster,
  ParkingPenaltyRateDetail,
  ParkingPenaltyRateDetailDto,
  ParkingSubRateOption,
  ParkingMainRateOption,
  ParkingPenaltyRateOption,
} from '../../models/system-settings/parking-master-data.model';
import { PagedResultDto } from '../../models/table.model';

@Injectable({ providedIn: 'root' })
export class MapperParkingMasterDataService {
  private parseDate(val: string | null | undefined): Date | null {
    if (!val) return null;
    const parsed = new Date(val);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private mapOdoo(dto: Record<string, unknown>, raw: Record<string, unknown>) {
    return {
      messageMainAttachmentId: (dto['messageMainAttachmentId'] ?? raw['MessageMainAttachmentId'] ?? null) as number | null,
      createUid: (dto['createUid'] ?? raw['CreateUid'] ?? null) as number | null,
      createDate: this.parseDate((dto['createDate'] ?? raw['CreateDate']) as string | undefined),
      writeUid: (dto['writeUid'] ?? raw['WriteUid'] ?? null) as number | null,
      writeDate: this.parseDate((dto['writeDate'] ?? raw['WriteDate']) as string | undefined),
    };
  }

  mapVehicleType(dto: ParkingVehicleTypeDto): ParkingVehicleType {
    const raw = dto as ParkingVehicleTypeDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      parkingTypeTaiwan: dto.parkingTypeTaiwan ?? (raw['ParkingTypeTaiwan'] as number | null) ?? null,
      remark: dto.remark ?? (raw['Remark'] as string) ?? '',
      updateBy: dto.updateBy ?? (raw['UpdateBy'] as string) ?? '',
      updateDate: this.parseDate(dto.updateDate ?? (raw['UpdateDate'] as string | undefined)),
      ...this.mapOdoo(dto as Record<string, unknown>, raw),
    };
  }

  mapCardType(dto: ParkingCardTypeDto): ParkingCardType {
    const raw = dto as ParkingCardTypeDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      vehicleTypeId: dto.vehicleTypeId ?? (raw['VehicleTypeId'] as number | null) ?? null,
      vehicleTypeName: dto.vehicleTypeName ?? (raw['VehicleTypeName'] as string) ?? '',
      member: (dto.member ?? raw['Member'] ?? false) as boolean,
      updateBy: dto.updateBy ?? (raw['UpdateBy'] as string) ?? '',
      updateDate: this.parseDate(dto.updateDate ?? (raw['UpdateDate'] as string | undefined)),
      ...this.mapOdoo(dto as Record<string, unknown>, raw),
    };
  }

  mapCardMode(dto: ParkingCardModeDto): ParkingCardMode {
    const raw = dto as ParkingCardModeDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      ...this.mapOdoo(dto as Record<string, unknown>, raw),
    };
  }

  mapPaymentLog(dto: ParkingPaymentLogDto): ParkingPaymentLog {
    const raw = dto as ParkingPaymentLogDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      ticketId: dto.ticketId ?? (raw['TicketId'] as string) ?? '',
      amount: dto.amount ?? (raw['Amount'] as number | null) ?? null,
      merchantOrderId: dto.merchantOrderId ?? (raw['MerchantOrderId'] as string) ?? '',
      urlFail: dto.urlFail ?? (raw['UrlFail'] as string) ?? '',
      urlSuccess: dto.urlSuccess ?? (raw['UrlSuccess'] as string) ?? '',
      ksherReturn: dto.ksherReturn ?? (raw['KsherReturn'] as string) ?? '',
      payDate: this.parseDate(dto.payDate ?? (raw['PayDate'] as string | undefined)),
      payRef: dto.payRef ?? (raw['PayRef'] as string) ?? '',
      parkingFee: dto.parkingFee ?? (raw['ParkingFee'] as number | null) ?? null,
      overNight: dto.overNight ?? (raw['OverNight'] as number | null) ?? null,
      lostCard: dto.lostCard ?? (raw['LostCard'] as number | null) ?? null,
      email: dto.email ?? (raw['Email'] as string) ?? '',
      qrcodeId: dto.qrcodeId ?? (raw['QrcodeId'] as string) ?? '',
      transactionId: dto.transactionId ?? (raw['TransactionId'] as number | null) ?? null,
      parkingFeeSub: dto.parkingFeeSub ?? (raw['ParkingFeeSub'] as number | null) ?? null,
      overNightSub: dto.overNightSub ?? (raw['OverNightSub'] as number | null) ?? null,
      companyId: dto.companyId ?? (raw['CompanyId'] as number) ?? 0,
      siteId: dto.siteId ?? (raw['SiteId'] as number | null) ?? null,
      siteName: dto.siteName ?? (raw['SiteName'] as string) ?? '',
      posId: dto.posId ?? (raw['PosId'] as number | null) ?? null,
      paymentLogType: dto.paymentLogType ?? (raw['PaymentLogType'] as string) ?? '',
      invoiceHeaderId: dto.invoiceHeaderId ?? (raw['InvoiceHeaderId'] as number | null) ?? null,
      ...this.mapOdoo(dto as Record<string, unknown>, raw),
    };
  }

  mapTable<TDto, TModel>(
    dto: unknown,
    mapper: (item: TDto) => TModel
  ): PagedResultDto<TModel> {
    const raw = dto as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as TDto[];
    return {
      data: list.map((item) => mapper(item)),
      total: (raw['total'] ?? raw['Total'] ?? 0) as number,
    };
  }

  mapActionResponse(res: ActionBackOfficeResponse | Record<string, unknown>) {
    const raw = res as Record<string, unknown>;
    return {
      isSuccess: !!(res.isSuccess ?? raw['IsSuccess']),
      message: String(res.message ?? raw['Message'] ?? ''),
      messageEn: String(res.messageEn ?? raw['MessageEn'] ?? ''),
      code: String(res.code ?? raw['Code'] ?? ''),
      idReturn: Number(res.IdReturn ?? raw['IdReturn'] ?? 0),
    };
  }

  createEmptyVehicleType(): ParkingVehicleType {
    return {
      id: 0,
      name: '',
      parkingTypeTaiwan: null,
      remark: '',
      updateBy: '',
      updateDate: null,
    };
  }

  createEmptyCardType(): ParkingCardType {
    return {
      id: 0,
      name: '',
      vehicleTypeId: null,
      vehicleTypeName: '',
      member: false,
      updateBy: '',
      updateDate: null,
    };
  }

  createEmptyCardMode(): ParkingCardMode {
    return { id: 0, name: '' };
  }

  mapVehicleTypePayload(form: ParkingVehicleType, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
      parkingTypeTaiwan: form.parkingTypeTaiwan,
      remark: form.remark?.trim() ?? '',
    };
  }

  mapCardTypePayload(form: ParkingCardType, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
      vehicleTypeId: form.vehicleTypeId,
      member: form.member,
    };
  }

  mapCardModePayload(form: ParkingCardMode, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
    };
  }

  mapGateType(dto: ParkingGateTypeDto): ParkingGateType {
    const raw = dto as ParkingGateTypeDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      updateBy: dto.updateBy ?? (raw['UpdateBy'] as string) ?? '',
      updateDate: this.parseDate(dto.updateDate ?? (raw['UpdateDate'] as string | undefined)),
      ...this.mapOdoo(dto as Record<string, unknown>, raw),
    };
  }

  mapGate(dto: ParkingGateDto): ParkingGate {
    const raw = dto as ParkingGateDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      detail: dto.detail ?? (raw['Detail'] as string) ?? '',
      posId: dto.posId ?? (raw['PosId'] as string) ?? '',
      ip: dto.ip ?? (raw['Ip'] as string) ?? '',
      gateTypeId: dto.gateTypeId ?? (raw['GateTypeId'] as number | null) ?? null,
      gateTypeName: dto.gateTypeName ?? (raw['GateTypeName'] as string) ?? '',
      areaId: dto.areaId ?? (raw['AreaId'] as number | null) ?? null,
      areaName: dto.areaName ?? (raw['AreaName'] as string) ?? '',
      vehicleTypeId: dto.vehicleTypeId ?? (raw['VehicleTypeId'] as number | null) ?? null,
      vehicleTypeName: dto.vehicleTypeName ?? (raw['VehicleTypeName'] as string) ?? '',
      apiUser: dto.apiUser ?? (raw['ApiUser'] as string) ?? '',
      apiPass: dto.apiPass ?? (raw['ApiPass'] as string) ?? '',
      connectDate: this.parseDate(dto.connectDate ?? (raw['ConnectDate'] as string | undefined)),
      monitor: dto.monitor ?? (raw['Monitor'] as string) ?? '',
      statusId: dto.statusId ?? (raw['StatusId'] as string) ?? '',
      deviceKey: dto.deviceKey ?? (raw['DeviceKey'] as string) ?? '',
      defaultRate: dto.defaultRate ?? (raw['DefaultRate'] as number | null) ?? null,
      defaultRateName: dto.defaultRateName ?? (raw['DefaultRateName'] as string) ?? '',
      prefix: dto.prefix ?? (raw['Prefix'] as string) ?? '',
      companyId: dto.companyId ?? (raw['CompanyId'] as number) ?? 0,
      siteId: dto.siteId ?? (raw['SiteId'] as number | null) ?? null,
      siteName: dto.siteName ?? (raw['SiteName'] as string) ?? '',
      sapName: dto.sapName ?? (raw['SapName'] as string) ?? '',
      sapNameM: dto.sapNameM ?? (raw['SapNameM'] as string) ?? '',
      checkCode: dto.checkCode ?? (raw['CheckCode'] as string) ?? '',
      evConnectorTypeId: dto.evConnectorTypeId ?? (raw['EvConnectorTypeId'] as number | null) ?? null,
      evConnectorTypeName: dto.evConnectorTypeName ?? (raw['EvConnectorTypeName'] as string) ?? '',
      powerKw: dto.powerKw ?? (raw['PowerKw'] as number | null) ?? null,
      displayName: dto.displayName ?? (raw['DisplayName'] as string) ?? '',
      customerVisible: (dto.customerVisible ?? raw['CustomerVisible'] ?? false) as boolean,
      staffVisible: (dto.staffVisible ?? raw['StaffVisible'] ?? false) as boolean,
      parentGateId: dto.parentGateId ?? (raw['ParentGateId'] as number | null) ?? null,
      parentGateName: dto.parentGateName ?? (raw['ParentGateName'] as string) ?? '',
      displayNames: dto.displayNames ?? (raw['DisplayNames'] as string) ?? '',
      taiwanGate: (dto.taiwanGate ?? raw['TaiwanGate'] ?? false) as boolean,
      online: (dto.online ?? raw['Online'] ?? false) as boolean,
      updateBy: dto.updateBy ?? (raw['UpdateBy'] as string) ?? '',
      updateDate: this.parseDate(dto.updateDate ?? (raw['UpdateDate'] as string | undefined)),
      ...this.mapOdoo(dto as Record<string, unknown>, raw),
    };
  }

  createEmptyGateType(): ParkingGateType {
    return { id: 0, name: '', updateBy: '', updateDate: null };
  }

  createEmptyGate(siteId: number | null = null, siteName = ''): ParkingGate {
    return {
      id: 0,
      name: '',
      detail: '',
      posId: '',
      ip: '',
      gateTypeId: null,
      gateTypeName: '',
      areaId: null,
      areaName: '',
      vehicleTypeId: null,
      vehicleTypeName: '',
      apiUser: '',
      apiPass: '',
      connectDate: null,
      monitor: '',
      statusId: '',
      deviceKey: '',
      defaultRate: null,
      defaultRateName: '',
      prefix: '',
      companyId: 1,
      siteId,
      siteName,
      sapName: '',
      sapNameM: '',
      checkCode: '',
      evConnectorTypeId: null,
      evConnectorTypeName: '',
      powerKw: null,
      displayName: '',
      customerVisible: true,
      staffVisible: true,
      parentGateId: null,
      parentGateName: '',
      displayNames: '',
      taiwanGate: false,
      online: false,
      updateBy: '',
      updateDate: null,
    };
  }

  mapGateTypePayload(form: ParkingGateType, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
    };
  }

  mapGatePayload(form: ParkingGate, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
      detail: form.detail?.trim() ?? '',
      posId: form.posId?.trim() ?? '',
      ip: form.ip?.trim() ?? '',
      gateTypeId: form.gateTypeId,
      areaId: form.areaId,
      vehicleTypeId: form.vehicleTypeId,
      apiUser: form.apiUser?.trim() ?? '',
      apiPass: form.apiPass ?? '',
      connectDate: form.connectDate ? form.connectDate.toISOString() : null,
      monitor: form.monitor?.trim() ?? '',
      statusId: form.statusId?.trim() ?? '',
      deviceKey: form.deviceKey?.trim() ?? '',
      defaultRate: form.defaultRate,
      prefix: form.prefix?.trim() ?? '',
      companyId: form.companyId ?? 0,
      siteId: form.siteId,
      sapName: form.sapName?.trim() ?? '',
      sapNameM: form.sapNameM?.trim() ?? '',
      checkCode: form.checkCode?.trim() ?? '',
      evConnectorTypeId: form.evConnectorTypeId,
      powerKw: form.powerKw,
      displayName: form.displayName?.trim() ?? '',
      customerVisible: !!form.customerVisible,
      staffVisible: !!form.staffVisible,
      parentGateId: form.parentGateId,
      displayNames: form.displayNames?.trim() ?? '',
      taiwanGate: !!form.taiwanGate,
      online: !!form.online,
    };
  }

  mapArea(dto: ParkingAreaDto): ParkingArea {
    const raw = dto as ParkingAreaDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      lot: dto.lot ?? (raw['Lot'] as string) ?? '',
      providerId: (dto.providerId ?? raw['ProviderId'] ?? null) as number | null,
      providerName: dto.providerName ?? (raw['ProviderName'] as string) ?? '',
      companyId: dto.companyId ?? (raw['CompanyId'] as number) ?? 1,
      siteId: (dto.siteId ?? raw['SiteId'] ?? null) as number | null,
      siteName: dto.siteName ?? (raw['SiteName'] as string) ?? '',
      vehicleTypeId: (dto.vehicleTypeId ?? raw['VehicleTypeId'] ?? null) as number | null,
      vehicleTypeName: dto.vehicleTypeName ?? (raw['VehicleTypeName'] as string) ?? '',
      isOnline: !!(dto.isOnline ?? raw['IsOnline']),
      hasImage1: !!(dto.hasImage1 ?? raw['HasImage1']),
      hasImage2: !!(dto.hasImage2 ?? raw['HasImage2']),
      hasImage3: !!(dto.hasImage3 ?? raw['HasImage3']),
      hasImage4: !!(dto.hasImage4 ?? raw['HasImage4']),
      image1Base64: dto.image1Base64 ?? (raw['Image1Base64'] as string) ?? '',
      image2Base64: dto.image2Base64 ?? (raw['Image2Base64'] as string) ?? '',
      image3Base64: dto.image3Base64 ?? (raw['Image3Base64'] as string) ?? '',
      image4Base64: dto.image4Base64 ?? (raw['Image4Base64'] as string) ?? '',
      updateBy: dto.updateBy ?? (raw['UpdateBy'] as string) ?? '',
      updateDate: this.parseDate((dto.updateDate ?? raw['UpdateDate']) as string | undefined),
      ...this.mapOdoo(dto as Record<string, unknown>, raw),
    };
  }

  createEmptyArea(siteId: number | null = null, siteName = ''): ParkingArea {
    return {
      id: 0,
      name: '',
      lot: '',
      providerId: null,
      providerName: '',
      companyId: 1,
      siteId,
      siteName,
      vehicleTypeId: null,
      vehicleTypeName: '',
      isOnline: true,
      hasImage1: false,
      hasImage2: false,
      hasImage3: false,
      hasImage4: false,
      image1Base64: '',
      image2Base64: '',
      image3Base64: '',
      image4Base64: '',
      updateBy: '',
      updateDate: null,
      messageMainAttachmentId: null,
      createUid: null,
      createDate: null,
      writeUid: null,
      writeDate: null,
    };
  }

  mapAreaPayload(form: ParkingArea, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
      lot: form.lot?.trim() ?? '',
      providerId: form.providerId,
      companyId: form.companyId || 1,
      siteId: form.siteId,
      vehicleTypeId: form.vehicleTypeId,
      isOnline: !!form.isOnline,
      image1Base64: form.image1Base64 || null,
      image2Base64: form.image2Base64 || null,
      image3Base64: form.image3Base64 || null,
      image4Base64: form.image4Base64 || null,
    };
  }

  mapProviderOptions(res: unknown): ParkingProviderOption[] {
    const raw = res as unknown;
    const list = Array.isArray(raw) ? raw : ((raw as { data?: unknown[] })?.data ?? []);
    return (list as Record<string, unknown>[]).map((row) => ({
      id: Number(row['id'] ?? row['Id'] ?? 0),
      name: String(row['name'] ?? row['Name'] ?? ''),
      siteId: (row['siteId'] ?? row['SiteId'] ?? null) as number | null,
    }));
  }

  mapGateGroup(dto: ParkingGateGroupDto): ParkingGateGroup {
    const raw = dto as ParkingGateGroupDto & Record<string, unknown>;
    const gateIdsRaw = dto.gateIds ?? (raw['GateIds'] as number[] | undefined) ?? [];
    const gatesRaw = (dto.gates ?? (raw['Gates'] as ParkingGateOption[] | undefined) ?? []) as unknown as Record<string, unknown>[];
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      detail: dto.detail ?? (raw['Detail'] as string) ?? '',
      gateNamesDisplay: dto.gateNamesDisplay ?? (raw['GateNamesDisplay'] as string) ?? '',
      gateIds: Array.isArray(gateIdsRaw) ? gateIdsRaw.map(Number).filter((x) => x > 0) : [],
      gateCount: dto.gateCount ?? (raw['GateCount'] as number) ?? 0,
      companyId: dto.companyId ?? (raw['CompanyId'] as number) ?? 1,
      siteId: (dto.siteId ?? raw['SiteId'] ?? null) as number | null,
      siteName: dto.siteName ?? (raw['SiteName'] as string) ?? '',
      updateBy: dto.updateBy ?? (raw['UpdateBy'] as string) ?? '',
      updateDate: this.parseDate((dto.updateDate ?? raw['UpdateDate']) as string | undefined),
      gates: gatesRaw.map((g) => ({
        id: Number(g['id'] ?? g['Id'] ?? 0),
        name: String(g['name'] ?? g['Name'] ?? ''),
        siteId: (g['siteId'] ?? g['SiteId'] ?? null) as number | null,
      })),
    };
  }

  createEmptyGateGroup(siteId: number | null = null, siteName = ''): ParkingGateGroup {
    return {
      id: 0,
      name: '',
      detail: '',
      gateNamesDisplay: '',
      gateIds: [],
      gateCount: 0,
      companyId: 1,
      siteId,
      siteName,
      updateBy: '',
      updateDate: null,
      gates: [],
    };
  }

  mapGateGroupPayload(form: ParkingGateGroup, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
      siteId: form.siteId ?? 0,
      companyId: form.companyId || 1,
      gateIds: [...(form.gateIds ?? [])].filter((id) => id > 0),
    };
  }

  mapGateOptions(res: unknown): ParkingGateOption[] {
    const raw = res as unknown;
    const list = Array.isArray(raw) ? raw : ((raw as { data?: unknown[] })?.data ?? []);
    return (list as Record<string, unknown>[]).map((row) => ({
      id: Number(row['id'] ?? row['Id'] ?? 0),
      name: String(row['name'] ?? row['Name'] ?? ''),
      siteId: (row['siteId'] ?? row['SiteId'] ?? null) as number | null,
    }));
  }

  mapRateMaster(dto: ParkingRateDto): ParkingRateMaster {
    const raw = dto as ParkingRateDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      rateId: dto.rateId ?? (raw['RateId'] as string) ?? '',
      name: dto.name ?? (raw['Name'] as string) ?? '',
      detail: dto.detail ?? (raw['Detail'] as string) ?? '',
      freeTime: (dto.freeTime ?? raw['FreeTime'] ?? null) as number | null,
      taiwanDiscountId: (dto.taiwanDiscountId ?? raw['TaiwanDiscountId'] ?? null) as number | null,
      taiwanDiscountName: dto.taiwanDiscountName ?? (raw['TaiwanDiscountName'] as string) ?? '',
      discount: (dto.discount ?? raw['Discount'] ?? false) as boolean,
      companyId: dto.companyId ?? (raw['CompanyId'] as number) ?? 1,
      siteId: (dto.siteId ?? raw['SiteId'] ?? null) as number | null,
      siteName: dto.siteName ?? (raw['SiteName'] as string) ?? '',
      updateBy: dto.updateBy ?? (raw['UpdateBy'] as string) ?? '',
      updateDate: this.parseDate((dto.updateDate ?? raw['UpdateDate']) as string | undefined),
      taiwanFineId: (dto.taiwanFineId ?? raw['TaiwanFineId'] ?? null) as number | null,
      taiwanFineName: dto.taiwanFineName ?? (raw['TaiwanFineName'] as string) ?? '',
      splitByVehicleType: !!(dto.splitByVehicleType ?? raw['SplitByVehicleType'] ?? false),
      taiwanDiscountId2: (dto.taiwanDiscountId2 ?? raw['TaiwanDiscountId2'] ?? null) as number | null,
      taiwanDiscountName2: dto.taiwanDiscountName2 ?? (raw['TaiwanDiscountName2'] as string) ?? '',
      taiwanFineId2: (dto.taiwanFineId2 ?? raw['TaiwanFineId2'] ?? null) as number | null,
      taiwanFineName2: dto.taiwanFineName2 ?? (raw['TaiwanFineName2'] as string) ?? '',
    };
  }

  createEmptyRateMaster(siteId: number | null = null, siteName = ''): ParkingRateMaster {
    return {
      id: 0,
      rateId: '',
      name: '',
      detail: '',
      freeTime: null,
      taiwanDiscountId: null,
      taiwanDiscountName: '',
      discount: false,
      companyId: 1,
      siteId,
      siteName,
      updateBy: '',
      updateDate: null,
      taiwanFineId: null,
      taiwanFineName: '',
      splitByVehicleType: false,
      taiwanDiscountId2: null,
      taiwanDiscountName2: '',
      taiwanFineId2: null,
      taiwanFineName2: '',
    };
  }

  mapRateMasterPayload(form: ParkingRateMaster, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      rateId: form.rateId?.trim() ?? '',
      name: form.name?.trim() ?? '',
      detail: form.detail?.trim() ?? '',
      freeTime: form.freeTime,
      taiwanDiscountId: form.taiwanDiscountId,
      discount: !!form.discount,
      siteId: form.siteId ?? 0,
      companyId: form.companyId || 1,
      taiwanFineId: form.taiwanFineId,
      splitByVehicleType: !!form.splitByVehicleType,
      taiwanDiscountId2: form.splitByVehicleType ? form.taiwanDiscountId2 : null,
      taiwanFineId2: form.splitByVehicleType ? form.taiwanFineId2 : null,
    };
  }

  mapTaiwanDiscountOptions(res: unknown): TaiwanDiscountOption[] {
    const raw = res as unknown;
    const list = Array.isArray(raw) ? raw : ((raw as { data?: unknown[] })?.data ?? []);
    return (list as Record<string, unknown>[]).map((row) => ({
      id: Number(row['id'] ?? row['Id'] ?? 0),
      name: String(row['name'] ?? row['Name'] ?? ''),
      carCategory: Number(row['carCategory'] ?? row['CarCategory'] ?? -1),
    }));
  }

  mapTaiwanCarCategoryOptions(res: unknown): TaiwanCarCategoryOption[] {
    const list = Array.isArray(res) ? res : ((res as { data?: unknown[] })?.data ?? []);
    return (list as Record<string, unknown>[]).map((row) => {
      const carCategory = Number(row['carCategory'] ?? row['CarCategory'] ?? 0);
      const names = (row['vehicleTypeNames'] ?? row['VehicleTypeNames'] ?? []) as string[];
      return {
        carCategory,
        vehicleTypeNames: names,
        label: names.length ? names.join(' / ') : `ประเภทรถ ${carCategory}`,
      };
    });
  }

  mapMainRate(dto: ParkingMainRateDto): ParkingMainRateMaster {
    const raw = dto as ParkingMainRateDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      mainRateId: dto.mainRateId ?? (raw['MainRateId'] as string) ?? '',
      rateCustomerCarId: dto.rateCustomerCarId ?? (raw['RateCustomerCarId'] as string) ?? '',
      rateCustomerBikeId: dto.rateCustomerBikeId ?? (raw['RateCustomerBikeId'] as string) ?? '',
      rateTenantCarId: dto.rateTenantCarId ?? (raw['RateTenantCarId'] as string) ?? '',
      rateTenantBikeId: dto.rateTenantBikeId ?? (raw['RateTenantBikeId'] as string) ?? '',
      minTenantAllowCar: (dto.minTenantAllowCar ?? raw['MinTenantAllowCar'] ?? null) as number | null,
      minTenantAllowBike: (dto.minTenantAllowBike ?? raw['MinTenantAllowBike'] ?? null) as number | null,
      penaltyCarId: dto.penaltyCarId ?? (raw['PenaltyCarId'] as string) ?? '',
      penaltyBikeId: dto.penaltyBikeId ?? (raw['PenaltyBikeId'] as string) ?? '',
      rateHolidayCarId: dto.rateHolidayCarId ?? (raw['RateHolidayCarId'] as string) ?? '',
      rateHolidayBikeId: dto.rateHolidayBikeId ?? (raw['RateHolidayBikeId'] as string) ?? '',
      commentTh: dto.commentTh ?? (raw['CommentTh'] as string) ?? '',
      commentEn: dto.commentEn ?? (raw['CommentEn'] as string) ?? '',
      rateCustomerCar: this.mapSubRateDetail(dto.rateCustomerCar ?? (raw['RateCustomerCar'] as ParkingSubRateDetailDto | null)),
      rateCustomerBike: this.mapSubRateDetail(dto.rateCustomerBike ?? (raw['RateCustomerBike'] as ParkingSubRateDetailDto | null)),
      rateTenantCar: this.mapSubRateDetail(dto.rateTenantCar ?? (raw['RateTenantCar'] as ParkingSubRateDetailDto | null)),
      rateTenantBike: this.mapSubRateDetail(dto.rateTenantBike ?? (raw['RateTenantBike'] as ParkingSubRateDetailDto | null)),
      rateHolidayCar: this.mapSubRateDetail(dto.rateHolidayCar ?? (raw['RateHolidayCar'] as ParkingSubRateDetailDto | null)),
      rateHolidayBike: this.mapSubRateDetail(dto.rateHolidayBike ?? (raw['RateHolidayBike'] as ParkingSubRateDetailDto | null)),
      penaltyCar: this.mapPenaltyRateDetail(dto.penaltyCar ?? (raw['PenaltyCar'] as ParkingPenaltyRateDetailDto | null)),
      penaltyBike: this.mapPenaltyRateDetail(dto.penaltyBike ?? (raw['PenaltyBike'] as ParkingPenaltyRateDetailDto | null)),
      ...this.mapOdoo(dto as unknown as Record<string, unknown>, raw),
    };
  }

  createEmptyMainRate(): ParkingMainRateMaster {
    return {
      id: 0,
      name: '',
      mainRateId: '',
      rateCustomerCarId: '',
      rateCustomerBikeId: '',
      rateTenantCarId: '',
      rateTenantBikeId: '',
      minTenantAllowCar: null,
      minTenantAllowBike: null,
      penaltyCarId: '',
      penaltyBikeId: '',
      rateHolidayCarId: '',
      rateHolidayBikeId: '',
      commentTh: '',
      commentEn: '',
      rateCustomerCar: null,
      rateCustomerBike: null,
      rateTenantCar: null,
      rateTenantBike: null,
      rateHolidayCar: null,
      rateHolidayBike: null,
      penaltyCar: null,
      penaltyBike: null,
    };
  }

  mapMainRatePayload(form: ParkingMainRateMaster, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
      mainRateId: form.mainRateId?.trim() ?? '',
      rateCustomerCarId: form.rateCustomerCarId || null,
      rateCustomerBikeId: form.rateCustomerBikeId || null,
      rateTenantCarId: form.rateTenantCarId || null,
      rateTenantBikeId: form.rateTenantBikeId || null,
      minTenantAllowCar: form.minTenantAllowCar,
      minTenantAllowBike: form.minTenantAllowBike,
      penaltyCarId: form.penaltyCarId || null,
      penaltyBikeId: form.penaltyBikeId || null,
      rateHolidayCarId: form.rateHolidayCarId || null,
      rateHolidayBikeId: form.rateHolidayBikeId || null,
      commentTh: form.commentTh?.trim() ?? '',
      commentEn: form.commentEn?.trim() ?? '',
    };
  }

  mapSubRate(dto: ParkingSubRateDto): ParkingSubRateMaster {
    const raw = dto as ParkingSubRateDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      rateId: dto.rateId ?? (raw['RateId'] as string) ?? '',
      rateType: dto.rateType ?? (raw['RateType'] as string) ?? '',
      subFreeTimeYn: dto.subFreeTimeYn ?? (raw['SubFreeTimeYn'] as string) ?? '',
      minFreeTime: (dto.minFreeTime ?? raw['MinFreeTime'] ?? null) as number | null,
      minRound: (dto.minRound ?? raw['MinRound'] ?? null) as number | null,
      timePeriodFree: dto.timePeriodFree ?? (raw['TimePeriodFree'] as string) ?? '',
      pricePerHour: (dto.pricePerHour ?? raw['PricePerHour'] ?? null) as number | null,
      hourOverFlat: (dto.hourOverFlat ?? raw['HourOverFlat'] ?? null) as number | null,
      priceFlat: (dto.priceFlat ?? raw['PriceFlat'] ?? null) as number | null,
      comment: dto.comment ?? (raw['Comment'] as string) ?? '',
      format: dto.format ?? (raw['Format'] as string) ?? '',
      serviceChargeFormat: dto.serviceChargeFormat ?? (raw['ServiceChargeFormat'] as string) ?? '',
      cutTime: dto.cutTime ?? (raw['CutTime'] as string) ?? '',
      ...this.mapOdoo(dto as unknown as Record<string, unknown>, raw),
    };
  }

  createEmptySubRate(): ParkingSubRateMaster {
    return {
      id: 0,
      name: '',
      rateId: '',
      rateType: '',
      subFreeTimeYn: '',
      minFreeTime: null,
      minRound: null,
      timePeriodFree: '',
      pricePerHour: null,
      hourOverFlat: null,
      priceFlat: null,
      comment: '',
      format: '',
      serviceChargeFormat: '',
      cutTime: '',
    };
  }

  mapSubRatePayload(form: ParkingSubRateMaster, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
      rateId: form.rateId?.trim() ?? '',
      rateType: form.rateType || null,
      subFreeTimeYn: form.subFreeTimeYn || null,
      minFreeTime: form.minFreeTime,
      minRound: form.minRound,
      timePeriodFree: form.timePeriodFree || null,
      pricePerHour: form.pricePerHour,
      hourOverFlat: form.hourOverFlat,
      priceFlat: form.priceFlat,
      comment: form.comment?.trim() ?? '',
      format: form.format || null,
      serviceChargeFormat: form.serviceChargeFormat || null,
      cutTime: form.cutTime || null,
    };
  }

  mapPenaltyRate(dto: ParkingPenaltyRateDto): ParkingPenaltyRateMaster {
    const raw = dto as ParkingPenaltyRateDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: dto.name ?? (raw['Name'] as string) ?? '',
      penaltyId: dto.penaltyId ?? (raw['PenaltyId'] as string) ?? '',
      overTime: dto.overTime ?? (raw['OverTime'] as string) ?? '',
      finePrice: (dto.finePrice ?? raw['FinePrice'] ?? null) as number | null,
      ...this.mapOdoo(dto as unknown as Record<string, unknown>, raw),
    };
  }

  createEmptyPenaltyRate(): ParkingPenaltyRateMaster {
    return { id: 0, name: '', penaltyId: '', overTime: '', finePrice: null };
  }

  mapPenaltyRatePayload(form: ParkingPenaltyRateMaster, userId: number, menuId: number) {
    return {
      userId,
      menuId,
      name: form.name?.trim() ?? '',
      penaltyId: form.penaltyId?.trim() ?? '',
      overTime: form.overTime || null,
      finePrice: form.finePrice,
    };
  }

  mapSubRateOptions(res: unknown): ParkingSubRateOption[] {
    const list = Array.isArray(res) ? res : ((res as { data?: unknown[] })?.data ?? []);
    return (list as Record<string, unknown>[])
      .map((row) => this.mapSubRateDetail(row as ParkingSubRateDetailDto))
      .filter((row): row is ParkingSubRateDetail => !!row && !!row.rateId);
  }

  mapMainRateOptions(res: unknown): ParkingMainRateOption[] {
    const list = Array.isArray(res) ? res : ((res as { data?: unknown[] })?.data ?? []);
    return (list as Record<string, unknown>[]).map((row) => ({
      mainRateId: String(row['mainRateId'] ?? row['MainRateId'] ?? ''),
      name: String(row['name'] ?? row['Name'] ?? ''),
    }));
  }

  mapPenaltyRateOptions(res: unknown): ParkingPenaltyRateOption[] {
    const list = Array.isArray(res) ? res : ((res as { data?: unknown[] })?.data ?? []);
    return (list as Record<string, unknown>[])
      .map((row) => this.mapPenaltyRateDetail(row as ParkingPenaltyRateDetailDto))
      .filter((row): row is ParkingPenaltyRateDetail => !!row && !!row.penaltyId);
  }

  mapSubRateDetail(dto: ParkingSubRateDetailDto | Record<string, unknown> | null | undefined): ParkingSubRateDetail | null {
    if (!dto) return null;
    const raw = dto as Record<string, unknown>;
    const rateId = String((dto as ParkingSubRateDetailDto).rateId ?? raw['RateId'] ?? raw['rateId'] ?? '');
    if (!rateId) return null;
    return {
      id: Number((dto as ParkingSubRateDetailDto).id ?? raw['Id'] ?? raw['id'] ?? 0),
      rateId,
      name: String((dto as ParkingSubRateDetailDto).name ?? raw['Name'] ?? raw['name'] ?? ''),
      rateType: String((dto as ParkingSubRateDetailDto).rateType ?? raw['RateType'] ?? raw['rateType'] ?? ''),
      subFreeTimeYn: String((dto as ParkingSubRateDetailDto).subFreeTimeYn ?? raw['SubFreeTimeYn'] ?? raw['subFreeTimeYn'] ?? ''),
      minFreeTime: ((dto as ParkingSubRateDetailDto).minFreeTime ?? raw['MinFreeTime'] ?? raw['minFreeTime'] ?? null) as number | null,
      minRound: ((dto as ParkingSubRateDetailDto).minRound ?? raw['MinRound'] ?? raw['minRound'] ?? null) as number | null,
      timePeriodFree: String((dto as ParkingSubRateDetailDto).timePeriodFree ?? raw['TimePeriodFree'] ?? raw['timePeriodFree'] ?? ''),
      pricePerHour: ((dto as ParkingSubRateDetailDto).pricePerHour ?? raw['PricePerHour'] ?? raw['pricePerHour'] ?? null) as number | null,
      hourOverFlat: ((dto as ParkingSubRateDetailDto).hourOverFlat ?? raw['HourOverFlat'] ?? raw['hourOverFlat'] ?? null) as number | null,
      priceFlat: ((dto as ParkingSubRateDetailDto).priceFlat ?? raw['PriceFlat'] ?? raw['priceFlat'] ?? null) as number | null,
      comment: String((dto as ParkingSubRateDetailDto).comment ?? raw['Comment'] ?? raw['comment'] ?? ''),
      format: String((dto as ParkingSubRateDetailDto).format ?? raw['Format'] ?? raw['format'] ?? ''),
      serviceChargeFormat: String((dto as ParkingSubRateDetailDto).serviceChargeFormat ?? raw['ServiceChargeFormat'] ?? raw['serviceChargeFormat'] ?? ''),
      cutTime: String((dto as ParkingSubRateDetailDto).cutTime ?? raw['CutTime'] ?? raw['cutTime'] ?? ''),
    };
  }

  mapPenaltyRateDetail(dto: ParkingPenaltyRateDetailDto | Record<string, unknown> | null | undefined): ParkingPenaltyRateDetail | null {
    if (!dto) return null;
    const raw = dto as Record<string, unknown>;
    const penaltyId = String((dto as ParkingPenaltyRateDetailDto).penaltyId ?? raw['PenaltyId'] ?? raw['penaltyId'] ?? '');
    if (!penaltyId) return null;
    return {
      id: Number((dto as ParkingPenaltyRateDetailDto).id ?? raw['Id'] ?? raw['id'] ?? 0),
      penaltyId,
      name: String((dto as ParkingPenaltyRateDetailDto).name ?? raw['Name'] ?? raw['name'] ?? ''),
      overTime: String((dto as ParkingPenaltyRateDetailDto).overTime ?? raw['OverTime'] ?? raw['overTime'] ?? ''),
      finePrice: ((dto as ParkingPenaltyRateDetailDto).finePrice ?? raw['FinePrice'] ?? raw['finePrice'] ?? null) as number | null,
    };
  }

  formatSubRateSummary(detail: ParkingSubRateDetail | null | undefined, fallbackId = ''): string {
    if (!detail) return fallbackId || '—';
    const price = detail.pricePerHour != null ? `${detail.pricePerHour}/ชม.` : (detail.priceFlat != null ? `flat ${detail.priceFlat}` : '');
    const parts = [detail.rateId || fallbackId, detail.name, price].filter(Boolean);
    return parts.join(' · ') || '—';
  }

  formatPenaltySummary(detail: ParkingPenaltyRateDetail | null | undefined, fallbackId = ''): string {
    if (!detail) return fallbackId || '—';
    const fine = detail.finePrice != null ? `฿${detail.finePrice}` : '';
    const overtime = detail.overTime ? `over ${detail.overTime}` : '';
    const parts = [detail.penaltyId || fallbackId, detail.name, fine, overtime].filter(Boolean);
    return parts.join(' · ') || '—';
  }
}
